import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { requisitionsApi, downloadAuthed, openAuthedInNewTab } from '../api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import ShipMarkModal from '../components/ShipMarkModal';
import WarehouseReviewModal from '../components/WarehouseReviewModal';
import { ArrowLeft, Download, Truck, PackageCheck, Loader2, Trash2, MessageSquare, Send, RotateCcw, XCircle, PauseCircle, CheckSquare, Square, Sparkles, FileText, Printer, ShieldCheck } from 'lucide-react';
import { fmtIST } from '../utils/datetime';
import { RejectionList } from '../components/RejectionsDialog';
import AcceptedNotice from '../components/AcceptedNotice';

/** Format an ISO datetime OR a DD/MM/YYYY string to DD/MM/YYYY, else '—'. */
const fmtDDMMYYYY = (v) => {
  if (!v) return '—';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d)) return '—';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
};

/** Format an ISO datetime as "DD MMM YYYY · HH:MM" for note timestamps. */
const fmtNoteTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CONDITION_LABEL = {
  new: 'New',
  empty: 'Empty',
  partial: 'Partially used',
};

const CONDITION_STYLE = {
  new: 'text-[#7a6f5c] bg-[#F7F4EA] border-[#E7E2D3]',
  empty: 'text-[#B84A4A] bg-[#FBEFEF] border-[#E8B8B8]',
  partial: 'text-[#8B6A1F] bg-[#FBF3DA] border-[#E8D28C]',
};

export default function RequisitionDetail() {
  const { id } = useParams();
  const { user, theme } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(-1);
  const [noteText, setNoteText] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const decoded = decodeURIComponent(id);
  const isSuper = user?.role === 'SuperAdmin';

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await requisitionsApi.get(decoded);
      setReq(r);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const markLine = async (idx, newStatus, reason) => {
    setBusy(idx);
    try {
      const patch = { lineStatus: newStatus };
      if (reason) patch.reason = reason;
      const updated = await requisitionsApi.updateLine(req.id, idx, patch);
      setReq(updated);
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(-1); }
  };

  const canMark = ['Warehouse', 'Admin', 'Logistics', 'SuperAdmin', 'AllIndiaManager'].includes(user?.role);
  const canDelete = user?.role === 'SuperAdmin';
  // iter37 — Warehouse per-line review is only available while the requisition
  // is still `Requested`. Warehouse + SuperAdmin can drive it from the UI.
  const canReview = ['Warehouse', 'SuperAdmin'].includes(user?.role) && req?.status === 'Requested' && !req?.isHistorical;

  /**
   * Sensible default status for the one-click "Mark requisition" modal
   * based on the caller's role AND the current rollup status. Keeps the
   * next-action button aligned with the workflow (no need to pick from a
   * long menu when only one action makes sense).
   */
  const defaultShipStatus = useMemo(() => {
    const role = user?.role;
    const cur = req?.status;
    if (role === 'Warehouse') return 'Fulfilled';
    if (role === 'Logistics') return cur === 'Shipped' ? 'Delivered' : 'Shipped';
    if (['Admin', 'SuperAdmin', 'AllIndiaManager'].includes(role)) {
      if (cur === 'Requested') return 'Fulfilled';
      if (cur === 'Fulfilled') return 'Shipped';
      if (cur === 'Shipped') return 'Delivered';
      return 'Fulfilled';
    }
    return 'Fulfilled';
  }, [user, req]);

  /** Pre-fill the modal with any tracking already stamped on the requisition
   *  so Logistics doesn't have to re-type what Warehouse captured. */
  const existingCourier = useMemo(
    () => (req?.lines || []).map(l => l.courier).find(Boolean) || '',
    [req],
  );
  const existingTracking = useMemo(
    () => (req?.lines || []).map(l => l.tracking).find(Boolean) || '',
    [req],
  );
  const shipModalCTA = user?.role === 'Warehouse'
    ? 'Mark Fulfilled'
    : user?.role === 'Logistics'
      ? (req?.status === 'Shipped' ? 'Mark Delivered' : 'Mark Shipped')
      : 'Mark requisition';

  // Waybill (delivery challan) PDF becomes available once Warehouse has
  // marked the requisition Fulfilled — before that the sheet's Tracking
  // and status fields would print blank. Any user who can view the
  // requisition can print it (needed by field/branch teams too).
  const canPrintWaybill = ['Fulfilled', 'Shipped', 'Delivered'].includes(req?.status);
  const [printingWaybill, setPrintingWaybill] = useState(false);
  const printWaybill = async () => {
    if (!req) return;
    setPrintingWaybill(true);
    try {
      await openAuthedInNewTab(requisitionsApi.waybillUrl(req.id));
    } catch (e) {
      alert(e.message || 'Could not open waybill');
    } finally {
      setPrintingWaybill(false);
    }
  };

  // Excel-like bulk edit state ------------------------------------------
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkCourier, setBulkCourier] = useState('');
  const [bulkTracking, setBulkTracking] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleLine = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const allLineIdx = useMemo(() => (req?.lines || []).map((_, i) => i), [req]);
  const allSelected = allLineIdx.length > 0 && allLineIdx.every(i => selected.has(i));
  const toggleAllLines = () => {
    setSelected(allSelected ? new Set() : new Set(allLineIdx));
  };

  const applyBulk = async () => {
    if (!bulkStatus || selected.size === 0) return;
    const needsReason = bulkStatus === 'Rejected' || bulkStatus === 'Held';
    if (needsReason && !bulkReason.trim()) {
      alert(`Please provide a reason for ${bulkStatus}`);
      return;
    }
    setBulkBusy(true);
    try {
      const body = {
        indices: [...selected],
        lineStatus: bulkStatus,
        ...(bulkReason.trim() ? { reason: bulkReason.trim() } : {}),
        ...(bulkCourier.trim() ? { courier: bulkCourier.trim() } : {}),
        ...(bulkTracking.trim() ? { tracking: bulkTracking.trim() } : {}),
      };
      const updated = await requisitionsApi.bulkLineUpdate(req.id, body);
      setReq(updated);
      setSelected(new Set());
      setBulkStatus(''); setBulkReason(''); setBulkCourier(''); setBulkTracking('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkBusy(false); }
  };

  const removeReq = async () => {
    if (!window.confirm(`Delete requisition ${req.id}? This cannot be undone.`)) return;
    try {
      await requisitionsApi.remove(req.id);
      navigate('/app/requisitions');
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const rollbackHistorical = async () => {
    if (!window.confirm(`Roll back historical entry ${req.id}? This soft-deletes it — you can restore later if needed. Live workflow requisitions are never touched.`)) return;
    setRollingBack(true);
    try {
      await requisitionsApi.softDeleteHistorical(req.id, 'SA historical rollback');
      navigate('/app/requisitions');
    } catch (e) {
      alert(e.response?.data?.detail || 'Rollback failed');
    } finally { setRollingBack(false); }
  };

  const postNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setPostingNote(true);
    try {
      const updated = await requisitionsApi.addNote(req.id, text);
      setReq(updated);
      setNoteText('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to add note');
    } finally { setPostingNote(false); }
  };

  const notes = useMemo(() => (req?.notes || []).slice().reverse(), [req]);

  if (loading) return <div className="p-10 text-center text-[#7a6f5c]"><Loader2 className="inline animate-spin" /></div>;
  if (err || !req) return (
    <div>
      <PageHeader title="Requisition not found" />
      <p className="text-[13px] text-[#a04747] mb-3">{err}</p>
      <Link to="/app/requisitions" className="text-[14px] underline" style={{ color: theme.primary }}>← Back to all requisitions</Link>
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-[13px] text-[#7a6f5c] hover:text-[#232323] mb-3">
        <ArrowLeft size={14} /> Back
      </button>
      <PageHeader
        title={<span className="font-mono text-[24px]">{req.id}</span>}
        subtitle={
          <span>
            {req.ebo} · {req.eboBranchCode} · raised by {req.raisedBy} on {req.date}
            {req.isHistorical && (
              <span
                data-testid="req-historical-chip"
                className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F7E9CC] text-[#8B6A1F] border border-[#E8D28C]"
                title={`Backfilled by ${req.backdatedByName || req.backdatedByEmail || 'unknown'} on ${fmtDDMMYYYY(req.backdatedAt)}`}
              >
                Historical entry — backfilled by {req.backdatedByName || req.backdatedByEmail || 'unknown'}
                {req.backdatedAt ? ` on ${fmtDDMMYYYY(req.backdatedAt)}` : ''}
              </span>
            )}
            {req.raisedOnBehalfOf && (
              <span
                data-testid="req-on-behalf-chip"
                className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E7F2E9] text-[#2f6b41] border border-[#B4D7B8]"
                title={`Raised by SuperAdmin ${req.raisedByAdminName || req.raisedByAdminEmail || 'unknown'} on behalf of this EBO`}
              >
                Raised on behalf by {req.raisedByAdminName || req.raisedByAdminEmail || 'SuperAdmin'}
              </span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {canReview && (
              <button
                onClick={() => setReviewModalOpen(true)}
                data-testid="req-detail-warehouse-review-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium text-white"
                style={{ background: '#4D7C5F' }}
                title="Accept, reject or hold each line before dispatch — required before Excel export"
              >
                <ShieldCheck size={14} /> Warehouse review
              </button>
            )}
            {canMark && req.status !== 'Delivered' && req.status !== 'Rejected' && (
              <button
                onClick={() => setShipModalOpen(true)}
                data-testid="req-detail-shipmark-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium text-white"
                style={{ background: theme.primary }}
                title="Stamp status + courier + tracking on every line in one click"
              >
                <Truck size={14} /> {shipModalCTA}
              </button>
            )}
            <button
              onClick={() => downloadAuthed(requisitionsApi.exportUrl(req.id), `${req.id}.xlsx`)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] transition-colors"
            >
              <Download size={14} /> Download Excel
            </button>
            {canPrintWaybill && (
              <button
                onClick={printWaybill}
                disabled={printingWaybill}
                data-testid="req-detail-print-waybill-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] transition-colors disabled:opacity-50"
                title="Open a printable A4 delivery challan / waybill PDF with the branch address + tracking + item list"
              >
                {printingWaybill ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
                Print Waybill
              </button>
            )}
            {isSuper && req.isHistorical && (
              <button
                data-testid="req-detail-rollback-btn"
                onClick={rollbackHistorical}
                disabled={rollingBack}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E8D28C] bg-[#FBF3DA] hover:bg-[#F7E9CC] disabled:opacity-50"
                style={{ color: '#8B6A1F' }}
                title="Soft-delete this historical entry — safe, reversible, does not touch live workflow requisitions"
              >
                {rollingBack ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                Roll back historical entry
              </button>
            )}
            {canDelete && (
              <button
                data-testid="req-detail-delete-btn"
                onClick={removeReq}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] transition-colors"
                style={{ color: '#B84A4A' }}
              >
                <Trash2 size={14} /> Delete requisition
              </button>
            )}
          </div>
        }
      />

      <ShipMarkModal
        open={shipModalOpen}
        onClose={() => setShipModalOpen(false)}
        reqId={req.id}
        initialStatus={defaultShipStatus}
        initialCourier={existingCourier}
        initialTracking={existingTracking}
        userRole={user?.role}
        onDone={(updated) => setReq(updated)}
      />

      <WarehouseReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        req={req}
        onDone={(updated) => setReq(updated)}
      />

      <div className="fm-card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[15px] font-semibold">Requisition status</div>
          <StatusBadge status={req.status} />
        </div>
        <div className="grid grid-cols-4 gap-2 text-[12px] text-[#7a6f5c]">
          {['Requested','Fulfilled','Shipped','Delivered'].map((s, idx) => {
            const step = ['Requested','Fulfilled','Shipped','Delivered'];
            const cur = step.indexOf(req.status);
            const active = idx <= cur;
            return (
              <div key={s} className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px]" style={{ background: active ? theme.primary : '#D8D2C2' }}>{idx+1}</div>
                <span className={active ? 'text-[#232323] font-medium' : ''}>{s}</span>
              </div>
            );
          })}
        </div>
        <div
          data-testid="req-timeline"
          className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4 pt-4 border-t border-[#EFEADC] text-[12.5px]"
        >
          <div>
            <div className="text-[#7a6f5c] uppercase tracking-wide text-[10.5px] mb-0.5">Requested</div>
            <div className="font-medium text-[#232323]" data-testid="req-timeline-requested">{fmtIST(req.createdAt) || req.date}</div>
          </div>
          <div>
            <div className="text-[#7a6f5c] uppercase tracking-wide text-[10.5px] mb-0.5">Accepted</div>
            <div className="font-medium text-[#232323]" data-testid="req-timeline-accepted">{fmtIST(req.warehouseReviewedAt) || '—'}</div>
          </div>
          <div>
            <div className="text-[#7a6f5c] uppercase tracking-wide text-[10.5px] mb-0.5">Fulfilled</div>
            <div className="font-medium text-[#232323]" data-testid="req-timeline-fulfilled">{fmtIST(req.fulfilledAt) || '—'}</div>
          </div>
          <div>
            <div className="text-[#7a6f5c] uppercase tracking-wide text-[10.5px] mb-0.5">Shipped</div>
            <div className="font-medium text-[#232323]" data-testid="req-timeline-shipped">{fmtIST(req.shippedAt || (req.lines?.find(l => l.shippedAt)?.shippedAt)) || '—'}</div>
          </div>
          <div>
            <div className="text-[#7a6f5c] uppercase tracking-wide text-[10.5px] mb-0.5">Delivered</div>
            <div className="font-medium text-[#232323]" data-testid="req-timeline-delivered">{fmtIST(req.deliveredAt) || '—'}</div>
          </div>
        </div>
      </div>

      <AcceptedNotice req={req} show={!!location.state?.justCreated} />

      {req.validation?.rejectedLines?.length > 0 && (
        <div className="fm-card p-5 mb-6" data-testid="req-rejected-lines-card">
          <div className="text-[13.5px] font-semibold mb-1">Lines not accepted at submission ({req.validation.rejectedLines.length})</div>
          <p className="text-[12px] text-[#7a6f5c] mb-3">These SKUs failed the Tester order checks and were left out of this requisition.</p>
          <RejectionList items={req.validation.rejectedLines} testPrefix="req-rejected-line" />
        </div>
      )}

      {req.remarks && (
        <div className="fm-card p-5 mb-6" data-testid="req-remarks-card">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={15} style={{ color: theme.primary }} />
            <h3 className="text-[14px] font-semibold text-[#232323]">Requisition remarks</h3>
          </div>
          <p className="text-[13.5px] text-[#3d3427] whitespace-pre-wrap break-words" data-testid="req-remarks-text">
            {req.remarks}
          </p>
        </div>
      )}

      <div className="fm-card fm-scroll-x">
        {canMark && (
          <div className="p-4 border-b border-[#EFEADC] bg-[#FAF7EE]" data-testid="req-bulk-toolbar">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="text-[12px] text-[#7a6f5c] mb-1.5" data-testid="req-bulk-selected-count">
                <span className="font-semibold text-[#232323]">{selected.size}</span> of {req.lines.length} lines selected
              </div>
              <select
                data-testid="req-bulk-status"
                value={bulkStatus}
                onChange={e => setBulkStatus(e.target.value)}
                className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white"
              >
                <option value="">Action…</option>
                <option value="Fulfilled">Process (Fulfilled)</option>
                <option value="Shipped">Ship (auto-share tracking)</option>
                <option value="Delivered">Delivered</option>
                <option value="Rejected">Reject</option>
                <option value="Held">Hold</option>
              </select>
              {(bulkStatus === 'Rejected' || bulkStatus === 'Held') && (
                <input
                  data-testid="req-bulk-reason"
                  type="text"
                  value={bulkReason}
                  onChange={e => setBulkReason(e.target.value)}
                  placeholder={`Reason for ${bulkStatus}…`}
                  className="fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[220px]"
                />
              )}
              {(bulkStatus === 'Shipped' || bulkStatus === 'Delivered') && (
                <>
                  <input
                    data-testid="req-bulk-courier"
                    type="text"
                    value={bulkCourier}
                    onChange={e => setBulkCourier(e.target.value)}
                    placeholder="Courier (defaults to Delhivery)"
                    className="fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[180px]"
                  />
                  <input
                    data-testid="req-bulk-tracking"
                    type="text"
                    value={bulkTracking}
                    onChange={e => setBulkTracking(e.target.value)}
                    placeholder="Tracking (shared across selected)"
                    className="fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[220px]"
                  />
                </>
              )}
              <button
                onClick={applyBulk}
                disabled={bulkBusy || !bulkStatus || selected.size === 0}
                data-testid="req-bulk-apply-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-40"
                style={{ background: theme.primary }}
              >
                {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                Apply to {selected.size}
              </button>
            </div>
            <div className="text-[11px] text-[#7a6f5c] mt-2">
              Reject / Hold require a reason. Shipped or Delivered share one tracking number across all selected lines — matching the courier reality of one requisition = one parcel.
            </div>
          </div>
        )}

        <table className="w-full text-[13.5px]" data-testid="req-lines-table">
          <thead>
            <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3]">
              {canMark && (
                <th className="px-3 py-3 w-[40px] font-medium">
                  <button onClick={toggleAllLines} data-testid="req-line-select-all" title={allSelected ? 'Deselect all' : 'Select all'}>
                    {allSelected ? <CheckSquare size={14} style={{ color: theme.primary }} /> : <Square size={14} />}
                  </button>
                </th>
              )}
              <th className="px-5 py-3 font-medium">SKU</th>
              <th className="px-5 py-3 font-medium">Product</th>
              <th className="px-5 py-3 font-medium text-center">Qty</th>
              <th className="px-5 py-3 font-medium">Condition</th>
              <th className="px-5 py-3 font-medium">Line status</th>
              <th className="px-5 py-3 font-medium">Reason</th>
              <th className="px-5 py-3 font-medium">Courier</th>
              <th className="px-5 py-3 font-medium">Tracking</th>
              {canMark && <th className="px-5 py-3 font-medium text-right pr-6">Quick</th>}
            </tr>
          </thead>
          <tbody>
            {req.lines.map((l, i) => {
              const on = selected.has(i);
              return (
                <tr key={i} className={`border-t border-[#EFEADC] ${on ? 'bg-[#FBF3DA]' : ''}`} data-testid={`req-line-${i}`}>
                  {canMark && (
                    <td className="px-3 py-3">
                      <button onClick={() => toggleLine(i)} data-testid={`req-line-checkbox-${i}`}>
                        {on ? <CheckSquare size={14} style={{ color: theme.primary }} /> : <Square size={14} />}
                      </button>
                    </td>
                  )}
                  <td className="px-5 py-3 font-mono text-[13px]">{l.sku}</td>
                  <td className="px-5 py-3">{l.product}</td>
                  <td className="px-5 py-3 text-center">{l.qty}</td>
                  <td className="px-5 py-3" data-testid={`req-line-condition-${i}`}>
                    {(() => {
                      const cond = String(l.condition || 'new').toLowerCase();
                      return (
                        <span className={`inline-block px-2 py-0.5 rounded-full border text-[11.5px] font-medium ${CONDITION_STYLE[cond] || CONDITION_STYLE.new}`}>
                          {CONDITION_LABEL[cond] || 'New'}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3"><StatusBadge status={l.lineStatus} small /></td>
                  <td className="px-5 py-3 text-[12.5px] text-[#7a6f5c] max-w-[180px]" title={l.reason}>{l.reason || <span className="text-[#b8ac96]">—</span>}</td>
                  <td className="px-5 py-3 text-[13px]">{l.courier || <span className="text-[#b8ac96]">—</span>}</td>
                  <td className="px-5 py-3 text-[13px] font-mono">{l.tracking || <span className="text-[#b8ac96]">—</span>}</td>
                  {canMark && (
                    <td className="px-5 py-3 text-right pr-6">
                      {busy === i ? <Loader2 className="animate-spin inline" size={14} /> : (
                        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                          {l.lineStatus === 'Requested' && (
                            <button onClick={() => markLine(i, 'Fulfilled')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] border border-[#E7E2D3] hover:bg-[#F7F4EA]"><PackageCheck size={12}/> Process</button>
                          )}
                          {l.lineStatus === 'Fulfilled' && (
                            <button onClick={() => markLine(i, 'Shipped')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] text-white" style={{ background: theme.primary }}><Truck size={12}/> Ship</button>
                          )}
                          {l.lineStatus === 'Shipped' && (
                            <button onClick={() => markLine(i, 'Delivered')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] text-white" style={{ background: '#4D7C5F' }}><PackageCheck size={12}/> Deliver</button>
                          )}
                          {l.lineStatus === 'Delivered' && <span className="text-[12px] text-[#7a6f5c]">Closed</span>}
                          {['Rejected','Held'].includes(l.lineStatus) && <span className="text-[12px]" style={{ color: l.lineStatus === 'Rejected' ? '#B84A4A' : '#8B6A1F' }}>{l.lineStatus}</span>}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="fm-card p-5 mt-6" data-testid="req-notes-card">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={15} style={{ color: theme.primary }} />
          <h3 className="text-[15px] font-semibold text-[#232323]">Notes</h3>
          <span className="text-[12px] text-[#7a6f5c] ml-auto" data-testid="req-notes-count">{notes.length} note{notes.length === 1 ? '' : 's'}</span>
        </div>

        <div className="space-y-2 mb-3">
          {notes.length === 0 && (
            <div className="text-[12.5px] text-[#7a6f5c] py-2">
              No notes yet — attach context here (delivered to store manager, damaged in transit, follow-up owed, etc.) so it stays on the timeline.
            </div>
          )}
          {notes.map((n, i) => (
            <div key={i} className="border border-[#EFEADC] rounded-md px-3 py-2 bg-[#FAF7EE]" data-testid={`req-note-${i}`}>
              <div className="flex items-center gap-2 text-[11.5px] text-[#7a6f5c] mb-1">
                <span className="font-semibold text-[#232323]">{n.byName || n.byEmail || 'Unknown'}</span>
                {n.byRole && <span>· {n.byRole}</span>}
                <span className="ml-auto" title={n.createdAt}>{fmtNoteTime(n.createdAt)}</span>
              </div>
              <div className="text-[13px] whitespace-pre-wrap break-words">{n.text}</div>
            </div>
          ))}
        </div>

        <div className="flex items-start gap-2">
          <textarea
            data-testid="req-note-input"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); postNote(); }
            }}
            placeholder="Add a note… (Cmd/Ctrl+Enter to submit)"
            maxLength={1000}
            rows={2}
            className="fm-focus flex-1 border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white"
          />
          <button
            onClick={postNote}
            disabled={!noteText.trim() || postingNote}
            data-testid="req-note-submit-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-50"
            style={{ background: theme.primary }}
          >
            {postingNote ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Post
          </button>
        </div>
      </div>
    </div>
  );
}
